import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import AuthLayout from '../components/AuthLayout';
import { Box, Button, Dialog, DialogContent, DialogTitle, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar } from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LaunchIcon from '@mui/icons-material/Launch';
import JobForm from '../components/JobForm';
import { sendApiRequest } from '../lib/api/public';
import { useSession } from '../components/SessionContext';
import { Drawer, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { BorderColor } from '@mui/icons-material';

const NEXT_PUBLIC_URL_WIDGETS = process.env.NEXT_PUBLIC_URL_WIDGETS;

const Jobs = () => {
  const router = useRouter();

  const { user } = useSession();
  const [isPromoting, setIsPromoting] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState();
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({ all: 0, open: 0, draft: 0, closed: 0 });
  const [showJobForm, setShowJobForm] = useState(false);
  const [installCode, setInstallCode] = useState('');
  const [showInstallCode, setShowInstallCode] = useState(false);
  const [showClearIcon, setShowClearIcon] = useState('none');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setShowClearIcon(value === "" ? "none" : "flex");
    router.push(`?search=${value}`);
  };

  const getJobs = () => {
    if (router.isReady) {
      const { query } = router;

      setLoading(true);

      sendApiRequest({
        path: `/jobs/get-list?status=${query.status}&search=${query.search || ''}`,
        method: 'GET',
      }).then((response: any) => {
        setLoading(false);

        if (!response.error) {
          setJobs(response);
        }
      })
    }
  }

  const getCounts = () => {
    sendApiRequest({
      path: `/jobs/count`,
      method: 'GET',
    }).then((response: any) => {
      if (!response.error) {
        setCounts(response);
      }
    }).catch(() => {
      setCounts({ all: 0, open: 0, draft: 0, closed: 0 });
    })
  }

  const promote = (jobId) => {
    setIsPromoting(true);

    sendApiRequest({
      path: `/jobs/promote`,
      data: { jobId }
    }).then((response) => {
      setIsPromoting(false);
      window.location.href = response.paymentUrl;
    })
  }

  const getCode = () => {
    setInstallCode(`
      <script>
        window.wibSettings = {
          companyId: "${user && user.companyId}",
        };

        (function () {
            var script = document.createElement('script');
            script.src = '${NEXT_PUBLIC_URL_WIDGETS}/jobsWidget.bundle.js';
            script.async = true;
            var entry = document.getElementsByTagName('script')[0];
            entry.parentNode.insertBefore(script, entry);
        })();
      </script>
    `)

    setShowInstallCode(true);
  }

  const { status, search } = router.query;

  useEffect(() => {
    if (!user) return;

    getJobs();
    getCounts();
  }, [router.isReady, status, search, user])

  const installCodeForm = () => {
    if (!showInstallCode) {
      return null;
    }

    return (
      <Dialog onClose={() => setShowInstallCode(false)} aria-labelledby="modal-title" open={true} maxWidth='md'>
        <DialogTitle id="modal-title">Install code</DialogTitle>

        <DialogContent>
          <pre>
            <code>
              {installCode}
            </code>
          </pre>
        </DialogContent>

      </Dialog>
    )
  }

  const renderJobForm = () => {
    if (!showJobForm) {
      return null;
    }

    return (
      <JobForm
        open={showJobForm}
        job={selectedJob}
        onClose={() => setShowJobForm(false)}
        onSuccess={() => { setShowJobForm(false); getJobs(); getCounts(); }}
      />
    )
  }

  const drawerWidth = 300;

  const renderListItem = (Icon, text, key) => {
    return (
      <Link href={`?status=${key === 'all' ? '' : key}`}>
        <ListItem
          sx={{
            cursor: 'pointer',
            bgcolor: status === key ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.08)',  // Change hover background color here
            }
          }}
        >
          <ListItemIcon
            sx={{
              color: 'black'
            }}
          >
            <Icon />
          </ListItemIcon>
          <ListItemText primary={text} />
          <span className='count'>{counts[key]}</span>
        </ListItem>
      </Link>
    )
  };

  const sideBar = (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: 15, // Ensures the drawer extends to the top of the viewport
          zIndex: (theme) => theme.zIndex.drawer  // Ensures the drawer stays below the AppBar
        },
      }}
    >
      <Toolbar />  {/* This empty Toolbar pushes content below the AppBar */}
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {renderListItem(AllInclusiveIcon, 'All', 'all')}
          {renderListItem(BusinessCenterIcon, 'Open', 'open')}
          {renderListItem(BorderColor, 'Drafts', 'draft')}
          {renderListItem(IndeterminateCheckBoxIcon, 'Closed', 'closed')}
        </List>
      </Box>
    </Drawer>
  )

  return (
    <>
      <Head>
        <title>Jobs | Work in biotech</title>
        <meta name="description" content="List of all jobs at app.workinbiotech.com" />
      </Head>

      <AuthLayout sideBar={sideBar} title='Jobs' isLoading={isLoading}>
        <div>
          <div
            style={{
              float: 'right',
            }}
          >
            <Button
              onClick={() => { setSelectedJob(null); setShowJobForm(true) }}
              variant="contained"
            >
              + Add job posting
            </Button>

            {'  '}

            <Button variant='contained' onClick={getCode} color="inherit">Get code</Button>

            {'  '}

            <Button variant='contained' target='__blank' href={`/embedded-view/companies/${user && user.companyId}`} color='inherit'>Embedded view</Button>
          </div>

          <TextField
            size="small"
            variant="outlined"
            value={search}
            onChange={handleSearchChange}
            style={{ width: '350px' }}
            placeholder='Search for job ...'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  style={{ display: showClearIcon }}
                >
                  <ClearIcon />
                </InputAdornment>
              )
            }}
          />

          <div style={{ clear: 'both' }} />

          <br />
          <br />

          {renderJobForm()}
          {installCodeForm()}

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job, index) => (
                  <TableRow
                    key={job._id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <span className={`circle-icon ${job.status}`} />

                      {job.title}

                      <Link href={job.jobUrl} target='__blank'>
                        <LaunchIcon sx={{ marginLeft: '5px', marginTop: '-5px', fontSize: '13px' }} />
                      </Link>

                      {
                        job.isFeatured && (
                          <span style={{ marginLeft: '20px', fontSize: '10px', fontWeight: 'bold', color: '#06993c', display: 'block' }}>Promoted</span>
                        )
                      }
                    </TableCell>
                    <TableCell>{job.location}</TableCell>
                    <TableCell align='right'>
                      {
                        (!job.createdOnWib) ? null : (
                          <>
                            <Button
                              variant='contained'
                              color="inherit"
                              onClick={() => {
                                const es = job.estimatedSalary || {};
                                const { minValue, maxValue } = es;

                                setSelectedJob({
                                  ...job,
                                  minSalary: minValue,
                                  maxSalary: maxValue
                                });
                                setShowJobForm(true)
                              }}>
                              Edit
                            </Button>

                            {'  '}
                          </>
                        )
                      }
                      {
                        !job.isFeatured && <Button variant='contained' disabled={isPromoting} onClick={promote.bind(this, job._id)} color="primary">Promote</Button>
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

        </div>
        <div className='clear' />
      </AuthLayout>
    </>
  );
};

export default Jobs;
